import * as yup from 'yup';

import { AdminSettingsResponse } from '../../../types';

import {
  transformAdminSettingsToFormValues,
  transformAdminSettingsToRequestValues
} from '../../../utils';

import { ADMIN_SETTINGS_GROUP } from 'RestAPIs/endpoints';
import { useSaveResource } from 'RestAPIs/useSaveResource';

import { useGetAdminSettings } from '../../../../queries';
import { AdminSettingsFormWrapper } from 'components/shared/AdminSettings/AdminSettingsFormWrapper';
import FormSkeleton from '../../Tables/CLA/PremiumPay/components/Form/components/FormSkeleton';
import Box from '@mui/material/Box';
import { BaseProfileForm } from './BaseProfileForm';
import { useTranslation } from 'react-i18next';
import { showErrorSnackbar } from 'components/shared/Snackbar';

export const SETTINGS_GROUP_NAME = 'negative-availability';

const schema = (t) =>
  yup.object({
    neg_hours_base: yup
      .number()
      .typeError(t('negativeAvailabilitySettings.required'))
      .min(0, t('negativeAvailabilitySettings.hoursInvalid'))
      .required(t('negativeAvailabilitySettings.required')),
    neg_hours_factor: yup
      .number()
      .typeError(t('negativeAvailabilitySettings.required'))
      .min(0, t('negativeAvailabilitySettings.percentInvalid'))
      .max(100, t('negativeAvailabilitySettings.percentInvalid'))
      .required(t('negativeAvailabilitySettings.required')),
    neg_hours_min_time_block: yup
      .number()
      .default(0)
      .typeError(t('negativeAvailabilitySettings.required'))
      .min(0, t('negativeAvailabilitySettings.hoursInvalid'))
      .required(t('negativeAvailabilitySettings.required'))
  });

const BaseProfile = () => {
  const { t } = useTranslation('common');
  const { data: negativeAvailability = [], isFetching } =
    useGetAdminSettings<AdminSettingsResponse>(SETTINGS_GROUP_NAME, {
      onError: () =>
        showErrorSnackbar(
          t('baseProfile.failedGetNegativeAvailabilitySettings')
        )
    });

  const { isLoading, mutate: save } = useSaveResource({
    url: ADMIN_SETTINGS_GROUP,
    id: negativeAvailability[0]?.groupId,
    invalidateQueryKey: [SETTINGS_GROUP_NAME],
    message: {
      success: t('baseProfile.savedNegativeAvailabilitySettings'),
      error: t('baseProfile.failedDuringSaveNegativeAvailabilitySettings')
    }
  });

  const settings = negativeAvailability[0] ?? undefined;

  const onSubmit = (data) => {
    const payload = {
      ...settings,
      settings: transformAdminSettingsToRequestValues(data, settings?.settings)
    };
    save(payload);
  };

  return (
    <>
      {settings ? (
        <AdminSettingsFormWrapper
          submitForm={onSubmit}
          schema={schema(t)}
          isFetching={isFetching}
          isLoading={isLoading}
          data={transformAdminSettingsToFormValues(settings)}
        >
          <BaseProfileForm />
        </AdminSettingsFormWrapper>
      ) : (
        <Box my={3}>
          <FormSkeleton />
        </Box>
      )}
    </>
  );
};

export default BaseProfile;
